<template lang="pug">
  v-container
    div(style="overflow: hidden")
    v-form(
      v-model="form"
      :disabled="!meDataState.isReady")
      v-row
        v-col
          input-image(
            v-model="meDataState.data.image"
            :hide-delete="meDataState.data.image.startsWith(`#`)"
            label="Photo"
            :image-width="100"
            :image-height="100"
            rounded)
      v-row
        v-col
          v-text-field(
            v-model="meDataState.data.name"
            filled
            :counter="30"
            :rules="[value => !!value || `Le nom est requis.`, $createLengthRule({ max: 30, label: `Le nom` })]"
            label="Pseudonyme")
      v-row
        v-col
          v-textarea(
            v-model="meDataState.data.description"
            :rows="5"
            :counter="800"
            filled
            :rules="[$createLengthRule({ max: 800, label: `La description` })]"
            label="Description")
      v-row
        v-col
          input-url(
            v-model="meDataState.data.link"
            label="Lien publique"
            :rules="[$createLengthRule({ max: 200, label: `Le lien` })]"
            :counter="200"
            filled)
      v-row
        v-col
          input-phone(
            v-model="meDataState.data.phoneNumber"
            label="Numéro de téléphone"
            :rules="[() => !!meDataState.data.phoneNumber || `Le numéro de téléphone est requis.`]"
            filled
            :disabled="true")
        v-col.text-center.mt-0.pt-0
          v-btn(@click="$createOnClick({ goToModifyPhoneNumber })") Modifier mon numéro de téléphone
      v-divider.my-6
      v-subheader Vos informations
      v-row
        v-col
          v-alert(
            type="info"
            :color="$theme.background.color") Les informations suivantes ne sont pas
            |  obligatoires mais aident au bon
            |  fonctionnement de l'application.
      v-row
        v-col
          v-text-field(
            v-model="meDataState.data.privateName"
            filled
            label="Prénom")
        v-col
          v-text-field(
            v-model="meDataState.data.privateSurname"
            filled
            label="Nom")
      v-row
        v-col
          input-choice(
            v-model="meDataState.data.kind"
            label="Genre"
            :items="[`Homme`, `Femme`, `Non précisé`]"
            filled)
      v-row
        v-col
          input-date(
            v-model="meDataState.data.birthday"
            label="Date de naissance"
            filled)
      v-subheader Où habitez-vous ?
      v-row
        v-col(:cols="6")
          v-text-field(
            v-model="meDataState.data.addressCity"
            label="Ville"
            filled)
        v-col(:cols="6")
          v-text-field(
            v-model="meDataState.data.addressDepartment"
            label="Département"
            filled)
      v-row
        v-col
          v-checkbox(
            v-model="meDataState.data.displayGivenSupports"
            label="Afficher le nombre de soutiens donnés")
      v-row(v-if="associationRequestItem")
        v-col.text-center
          utility-action-state(:action="associationRequestItem.onClick")
            template(#default="{ trigger, loading }")
              v-btn(
                @click="trigger"
                outlined
                :loading="loading")
                v-icon(left) {{ associationRequestItem.icon }}
                | {{ associationRequestItem.label }}
      template(v-else)
        v-divider.mt-4
        v-subheader Votre association
        v-row
          v-col
            input-phone(
              v-model="associationDataState.data.phoneNumber"
              disabled
              permanent-hint
              hint="Pas encore opérationnel"
              :rules="[value => !!value || `Le numéro de téléphone est requis.`]")
        v-row
          v-col
            input-date(
              v-model="associationDataState.data.creationDate"
              label="Date de création"
              filled)
        v-row
          v-col
            input-multiple(
              v-model="associationDataState.data.themes"
              label="Thèmes"
              :items="associationDataState.items.themes"
              multiple
              filled
              :max="3")
        v-subheader Où votre association agit-elle ?
        v-row
          v-col
            v-text-field(
              v-model="associationDataState.data.operatingCountry"
              label="Pays"
              filled)
          v-col
            v-text-field(
              v-model="associationDataState.data.operatingDepartment"
              label="Département"
              filled)

</template>

<script>
import { page } from '@iez/core'
import router from '@iez/core/router/'
import data from '@iez/lib/data/'
import user from '@iez/lib/user/'
import { toRefs, reactive, computed } from '@vue/composition-api'
import InputImage from '@iez/components/inputs/image/'
import InputError from '@iez/components/inputs/error/'
import fromDataState from '@iez/lib/from-data-state/'
import getAssociationRequestItem from '@/shared/get-association-request-item/'
import UtilityActionState from '@iez/components/utility/action-state/'
import InputDate from '@iez/components/inputs/date/'
import InputMultiple from '@iez/components/inputs/multiple/'
import InputChoice from '@iez/components/inputs/choice/'
import InputUrl from '@iez/components/inputs/url/'
import InputPhone from '@iez/components/inputs/phone/'
import chroma from 'chroma-js'

const route = {
  path: '/profile-edit',
  name: 'ProfileEdit'
}

export default {
  ...route,

  components: {
    InputError,
    InputImage,
    UtilityActionState,
    InputDate,
    InputMultiple,
    InputChoice,
    InputUrl,
    InputPhone
  },

  setup () {
    page.topBar.set({
      title: computed(
        () => state.isNewUser
          ? `Création du profil`
          : `Modification du profil`
      )
    })

    const associationRequestItem = getAssociationRequestItem()

    const associationFields = [
      `id`,
      `phoneNumber`,
      `creationDate`,
      `operatingCountry`,
      `operatingDepartment`, {
        associationsThemes: [
          `id`,
          `themeId`
        ]
      }
    ]
    const fields = [
      'id',
      `phoneNumber`,
      'name',
      'description',
      `link`,
      `privateName`,
      `privateSurname`,
      `kind`,
      `birthday`,
      `addressCity`,
      `addressDepartment`,
      `displayGivenSupports`, {
        image: [
          `id`,
          `src`
        ],
        association: associationFields
      }
    ]
    const state = reactive({
      form: false,
      me: data.me({
        fields
      }),
      isNewUser: computed(
        () => !state.me?.name
      ),
      meDataState: fromDataState({
        getId: () => state.me?.id,
        props: {
          ...Object.fromEntries(
            [
              `name`,
              `description`,
              `link`,
              `privateName`,
              `privateSurname`,
              `kind`,
              `birthday`,
              `addressCity`,
              `addressDepartment`,
              `displayGivenSupports`,
              `phoneNumber`
            ].map(
              prop => [
                prop, {
                  get: () => state.me[prop]
                }
              ]
            )
          ),
          image: {
            type: `image`,
            get: () => state.me.image,
            defaultValue: chroma.random().hex()
          }
        }
      }),
      associationDataState: fromDataState({
        getId: () => state.me?.association?.id,
        props: {
          ...Object.fromEntries(
            [
              `creationDate`
            ].map(
              prop => [
                prop, {
                  get: () => state.me.association[prop]
                }
              ]
            )
          ),
          themes: {
            get: () => state.me.association.associationsThemes,
            aggregation: {
              root: {
                name: `associations`, // for remove (evict from root)
                idProp: `associationId` // for add (parent id)
              },
              link: {
                name: `associationsThemes` // for add and remove
              },
              child: {
                name: `themes`, // for items fetching
                idProp: `themeId`, // for add + hydrate snapshot
                labelProp: `name`
              }
            }
          },
          phoneNumber: {
            get: () => state.me.association.phoneNumber,
            default: ``
          },
          operatingDepartment: {
            get: () => state.me.association.operatingDepartment,
            default: ``
          },
          operatingCountry: {
            get: () => state.me.association.operatingCountry,
            default: ``
          }
        }
      })
    })

    const save = async () => {
      const wasNewUser = state.isNewUser

      if (state.associationDataState.hasChanged) {
        const _setAssociation = await state.associationDataState.smartPatch()

        console.log('_setassociation', _setAssociation)
        await data.update({
          root: `associations`,
          id: state.me.association.id,
          _set: _setAssociation,
          fields: associationFields
        })
      }

      if (state.meDataState.hasChanged) {
        const _set = await state.meDataState.smartPatch()
        await data.update({
          root: `users`,
          id: user.id,
          _set,
          fields
        })
        console.log('Updated')
      }

      if (
        state.meDataState.hasChanged ||
        state.associationDataState.hasChanged
      ) {
        page.bottomSnackbar.set({
          content: wasNewUser
            ? `Profil créé !`
            : `Profil modifié !`,
          afterEnter: true
        })
      }

      if (wasNewUser) {
        router.push({
          name: `Home`
        })
      } else {
        router.back()
      }
    }

    page.bottomAction.set(
      () =>
        state.form && (
          state.meDataState.hasChanged ||
          state.associationDataState.hasChanged
        ) ? ({
          action: {
            label: `Enregistrer`,
            onClick: save
          }
        }) : null
    )

    const goToModifyPhoneNumber = () => {
      router.push({
        name: `ModifyPhoneNumber`
      })
    }

    return {
      ...toRefs(state),
      save,
      associationRequestItem,
      goToModifyPhoneNumber
    }
  }
}

</script>